import { isArrayEmpty } from 'util/isArrayEmpty';

import { ClientRoute } from 'app/route/client';
import { FeatureFlags } from 'config/featureFlags';
import { growthbook } from 'config/growthbook';
import { createLoadingNode, NavigationNode } from 'model/NavigationNode';
import { ProjectModel } from 'api/dto/Project.dto';
import { PulseTemplateModel } from 'api/dto/PulseTemplate.dto';
import { compilePath } from 'router/compilePath';

import { generateLifecycleSurveysLinks } from './generateLifecycleSurveysLinks';

const questionLibraryLink = (id: number): NavigationNode => ({
  id: 'pulseSurveyQuestionLibrary',
  label: 'Question Library',
  href: compilePath(ClientRoute.Project.Survey.QUESTION_LIBRARY, { id }),
  pattern: ClientRoute.Project.Survey.QUESTION_LIBRARY,
});

const createNewPulseSurveyLink = (id: number): NavigationNode => ({
  id: 'createNewPulseSurvey',
  label: 'Create New Pulse Survey',
  href: compilePath(ClientRoute.Project.Survey.CREATE, { id }),
  pattern: ClientRoute.Project.Survey.CREATE,
});

const generateQuestionLibraryLink = (
  id: number,
  inDevelopmentProjects: ProjectModel[],
  liveProjects: ProjectModel[],
): NavigationNode[] =>
  !isArrayEmpty(inDevelopmentProjects) || !isArrayEmpty(liveProjects)
    ? [questionLibraryLink(id)]
    : [];

const generateSurveyCreationLinks = (
  id: number,
  inDevelopmentProjects: ProjectModel[],
  liveProjects: ProjectModel[],
  isPulseTemplateFullyLoaded: boolean,
): NavigationNode[] => {
  if (growthbook.isOn(FeatureFlags.FLEXIBLE_SURVEY_CREATION)) {
    return [questionLibraryLink(id), createNewPulseSurveyLink(id)];
  }

  return isPulseTemplateFullyLoaded
    ? generateQuestionLibraryLink(id, inDevelopmentProjects, liveProjects)
    : [];
};

const liveAndScheduledSurveysLink = (id: number): NavigationNode => ({
  id: 'liveAndScheduledSurveys',
  label: 'Live and Scheduled Surveys',
  href: compilePath(ClientRoute.Project.Survey.LIVE_AND_SCHEDULED, { id }),
  pattern: ClientRoute.Project.Survey.LIVE_AND_SCHEDULED,
});

const hasLiveAndScheduledSurveys = (pulseTemplate: PulseTemplateModel): boolean =>
  growthbook.isOn(FeatureFlags.FLEXIBLE_SURVEY_CREATION)
    ? pulseTemplate.pulseProjects.length > 0
    : PulseTemplateModel.getLiveAndScheduledSurveys(pulseTemplate).length > 0;

const generateSurveyListLinks = (
  id: number,
  pulseTemplate: PulseTemplateModel,
  isPulseTemplateFullyLoaded: boolean,
): NavigationNode[] => {
  if (!isPulseTemplateFullyLoaded) {
    return [
      createLoadingNode('engagePulseSurveysLoader', 'Loading surveys', [
        ...(growthbook.isOn(FeatureFlags.FLEXIBLE_SURVEY_CREATION)
          ? []
          : [ClientRoute.Project.Survey.QUESTION_LIBRARY]),
        ClientRoute.Project.Survey.LIVE_AND_SCHEDULED,
        ClientRoute.Project.Survey.ARCHIVED,
      ]),
    ];
  }

  return [
    ...(hasLiveAndScheduledSurveys(pulseTemplate) ? [liveAndScheduledSurveysLink(id)] : []),
    ...(PulseTemplateModel.getArchivedSurveys(pulseTemplate).length > 0
      ? [
          {
            id: 'archivedSurveys',
            label: 'Archived Surveys',
            href: compilePath(ClientRoute.Project.Survey.ARCHIVED, { id }),
            pattern: ClientRoute.Project.Survey.ARCHIVED,
          },
        ]
      : []),
  ];
};

const generateDemandPollListLinks = (
  id: number,
  project: PulseTemplateModel,
  isPulseTemplateFullyLoaded: boolean,
): NavigationNode[] => {
  if (!isPulseTemplateFullyLoaded) {
    return [
      createLoadingNode('onDemandPollsLoader', 'Loading polls', [
        ClientRoute.Project.DemandPoll.LIVE_AND_SCHEDULED,
        ClientRoute.Project.DemandPoll.ARCHIVED,
      ]),
    ];
  }

  return [
    ...(PulseTemplateModel.getLiveAndScheduledDemandPolls(project).length > 0
      ? [
          {
            id: 'liveAndScheduledODP',
            label: 'Live and Scheduled Polls',
            href: compilePath(ClientRoute.Project.DemandPoll.LIVE_AND_SCHEDULED, { id }),
            pattern: ClientRoute.Project.DemandPoll.LIVE_AND_SCHEDULED,
          },
        ]
      : []),
    ...(PulseTemplateModel.getArchivedDemandPolls(project).length > 0
      ? [
          {
            id: 'archivedODP',
            label: 'Archived Polls',
            href: compilePath(ClientRoute.Project.DemandPoll.ARCHIVED, { id }),
            pattern: ClientRoute.Project.DemandPoll.ARCHIVED,
          },
        ]
      : []),
  ];
};

const generateDemandPolls = (
  id: number,
  project: PulseTemplateModel,
  isPulseTemplateFullyLoaded: boolean,
): NavigationNode[] => [
  {
    id: 'onDemandPolls',
    label: 'On Demand Polls',
    children: [
      {
        id: 'createODP',
        label: 'Create New Poll',
        href: compilePath(ClientRoute.Project.DemandPoll.CREATE, { id }),
        pattern: ClientRoute.Project.DemandPoll.CREATE,
      },
      ...generateDemandPollListLinks(id, project, isPulseTemplateFullyLoaded),
    ],
  },
];

const getOnlyLinksForAdmin = (id: number, isClientAdmin: boolean): NavigationNode[] =>
  !isClientAdmin
    ? [
        {
          id: 'accountSettings',
          label: 'Account Settings',
          href: compilePath(ClientRoute.Project.ACCOUNT_SETTINGS, { id }),
          pattern: ClientRoute.Project.ACCOUNT_SETTINGS,
        },
        {
          id: 'trending',
          label: 'Trending',
          children: [
            {
              id: 'benchmarkData',
              label: 'Benchmark Data',
              href: compilePath(ClientRoute.Project.Trending.BENCHMARK_DATA_PATTERN, { id }),
              pattern: ClientRoute.Project.Trending.BENCHMARK_DATA_PATTERN,
            },
            {
              id: 'trendData',
              label: 'Trend Data',
              href: compilePath(ClientRoute.Project.Trending.TREND_DATA_PATTERN, { id }),
              pattern: ClientRoute.Project.Trending.TREND_DATA_PATTERN,
            },
          ],
        },
        {
          id: 'data',
          label: 'Data',
          children: [
            {
              id: 'dataExport',
              label: 'Data Export',
              href: compilePath(ClientRoute.Project.Data.EXPORT_PATTERN, { id }),
              pattern: ClientRoute.Project.Data.EXPORT_PATTERN,
            },
            {
              id: 'downloads',
              label: 'Downloads',
              href: compilePath(ClientRoute.Project.Data.DOWNLOADS_PATTERN, { id }),
              pattern: ClientRoute.Project.Data.DOWNLOADS_PATTERN,
            },
            {
              id: 'importSurvey',
              label: 'Import Survey',
              href: compilePath(ClientRoute.Project.Data.IMPORT_SURVEY_PATTERN, { id }),
              pattern: ClientRoute.Project.Data.IMPORT_SURVEY_PATTERN,
            },
            {
              id: 'manageSurveys',
              label: 'Manage surveys',
              href: compilePath(ClientRoute.Project.Data.MANAGE_SURVEY_PATTERN, { id }),
              pattern: ClientRoute.Project.Data.MANAGE_SURVEY_PATTERN,
              requiredRoles: ['ROLE_PROJECT_DATE_CHANGER'],
            },
          ],
        },
      ]
    : [];

type ProjectLinksConfig = {
  project: PulseTemplateModel;
  inDevelopmentPulseProjects: ProjectModel[];
  livePulseProjects: ProjectModel[];
  isClientAdmin: boolean;
  isEssentialPlan: boolean;
  pulseLifecycleProjects: ProjectModel[];
  isMultiLanguageEnabled: boolean;
  isHrisApiEnabled: boolean;
  isLifecycleSurveysEnabled: boolean;
  isPulseTemplateFullyLoaded: boolean;
};

export namespace ClientLeftMenuLinks {
  export const project = ({
    project: pulseTemplate,
    inDevelopmentPulseProjects,
    livePulseProjects,
    isClientAdmin,
    isEssentialPlan,
    pulseLifecycleProjects,
    isMultiLanguageEnabled,
    isHrisApiEnabled,
    isLifecycleSurveysEnabled,
    isPulseTemplateFullyLoaded,
  }: ProjectLinksConfig): NavigationNode[] => {
    const pulseTemplateProject = pulseTemplate.project;
    const id = pulseTemplateProject?.id;
    const isKioskDistributionEmpty = pulseTemplateProject.isKioskDistributionEmpty;
    return [
      {
        id: 'companySettings',
        label: 'Company',
        children: [
          {
            id: 'profile',
            label: 'Company Profile',
            href: compilePath(ClientRoute.Project.COMPANY_PROFILE, { id }),
            pattern: ClientRoute.Project.COMPANY_PROFILE,
          },
          {
            id: 'settings',
            label: 'Settings',
            href: compilePath(ClientRoute.Project.COMPANY_SETTINGS, { id }),
            pattern: ClientRoute.Project.COMPANY_SETTINGS,
          },
          ...(isMultiLanguageEnabled
            ? [
                {
                  id: 'attributes',
                  label: 'Languages',
                  href: compilePath(ClientRoute.Project.Languages.SURVEY, { id }),
                  pattern: ClientRoute.Project.Languages.SURVEY,
                },
              ]
            : []),
          ...(isHrisApiEnabled
            ? [
                {
                  id: 'integrations',
                  label: 'Integrations',
                  href: compilePath(ClientRoute.Project.INTEGRATIONS, { id }),
                  pattern: ClientRoute.Project.INTEGRATIONS,
                },
              ]
            : []),
        ],
      },
      {
        id: 'people',
        label: 'People',
        children: [
          {
            id: 'attributes',
            label: 'Attributes',
            href: compilePath(ClientRoute.Project.People.ATTRIBUTES_DEMOGRAPHICS, { id }),
            pattern: ClientRoute.Project.People.ATTRIBUTES_PATTERN,
          },
          {
            id: 'employees',
            label: 'Employees',
            href: compilePath(ClientRoute.Project.People.EMPLOYEES, { id }),
            pattern: ClientRoute.Project.People.EMPLOYEES,
          },
          {
            id: 'accessControl',
            label: 'Access Control',
            href: compilePath(
              isKioskDistributionEmpty
                ? ClientRoute.Project.People.USERS_PATTERN
                : ClientRoute.Project.People.KIOSK_PATTERN,
              { id },
            ),
            pattern: ClientRoute.Project.People.DASHBOARD_PERMISSIONS_PATTERN,
          },
        ],
      },
      {
        id: 'engagePulseSurveys',
        label: 'Engage (Pulse Surveys)',
        children: [
          ...generateSurveyCreationLinks(
            id,
            inDevelopmentPulseProjects,
            livePulseProjects,
            isPulseTemplateFullyLoaded,
          ),
          ...generateSurveyListLinks(id, pulseTemplate, isPulseTemplateFullyLoaded),
          {
            id: 'pulseSurveySettings',
            label: 'Survey Settings',
            href: compilePath(ClientRoute.Project.Survey.SURVEY_SETTINGS, { id }),
            pattern: ClientRoute.Project.Survey.SURVEY_SETTINGS,
          },
        ],
      },
      ...(!isEssentialPlan
        ? generateDemandPolls(id, pulseTemplate, isPulseTemplateFullyLoaded)
        : []),
      ...generateLifecycleSurveysLinks(
        id,
        pulseLifecycleProjects,
        isLifecycleSurveysEnabled,
        isPulseTemplateFullyLoaded,
      ),
      ...getOnlyLinksForAdmin(id, isClientAdmin),
    ];
  };
}
